import { Component, Input, OnInit, OnDestroy, AfterViewChecked, ViewChild, ElementRef, Renderer2 } from '@angular/core';
import { buildDashboardThemeVars } from '../../shared/dashboard-widget-theme';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReportsService } from '../../services/reports.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { DashboardService } from '../../services/dashboard.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SalesTargetEditorComponent } from '../../shared/sales-target-editor/sales-target-editor.component';

export interface DayCell {
	date: Date;
	dayNum: number;
	isCurrentMonth: boolean;
	isToday: boolean;
	dollars: number;
	units: number;
	invoices: number;
	priorDollars: number;
	priorUnits: number;
	dollarPct: number | null;   // % change vs prior year, null = no prior data
	intensity: number;          // 0–1 heat-map
}

export type DrillLevel = 'brands' | 'customers' | 'invoices' | 'lines' | 'salesmen' | 'sm-customers' | 'sm-invoices' | 'sm-lines';
export type DrillMode  = 'brand' | 'salesman';

@Component({
	selector: 'app-sales-calendar',
	templateUrl: './sales-calendar.component.html',
	styleUrls: ['./sales-calendar.component.scss'],
})
export class SalesCalendarComponent implements OnInit, OnDestroy, AfterViewChecked {

	@ViewChild('drillBackdrop') drillBackdropEl?: ElementRef<HTMLElement>;

	/** Optional month to open on (any value accepted by `new Date()`). When set
	 *  — e.g. from the This/Last widget clicking a month — the calendar lands on
	 *  that month instead of the current one. */
	@Input() initialDate: Date | string | false = false;

	// ── Calendar state ──────────────────────────────────────────────────
	viewDate  = new Date();
	weeks: DayCell[][] = [];
	loading   = true;

	totalMonth  = 0;

	/** Monthly sales targets for this user, keyed 'YYYY-MM'. */
	targetsByPeriod: Record<string, number> = {};
	isSalesmanUser = false;
	private userData: any = null;
	/** Per-week progress vs the month target — rebuilt with the calendar. */
	weekSummaries: Array<{ label: string; total: number; cumTotal: number; cumPct: number; expectedPct: number; started: boolean; isCurrent: boolean }> = [];
	totalUnits  = 0;
	bestDay     = 0;
	bestDayDate: Date | null = null;
	/** Highest single-day unit count in the month — scales the heat-map when
	 *  units lead (tires package). */
	bestUnits   = 0;
	/** Date of the highest-units day — the "Best Day" sub-line when units lead. */
	bestUnitsDate: Date | null = null;
	/** Tires lead with units over dollars: headline metric, heat-map and day
	 *  ranking key off units instead of sales. Driven by config.env.package. */
	unitsPrimary = false;
	get avgUnits(): number {
		return this.salesDays ? this.totalUnits / this.salesDays : 0;
	}
	salesDays   = 0;
	avgDay      = 0;
	monthPct: number | null = null;   // vs prior year same month

	private rawData: { trandate: string; invoiced: number; dollars: number; invoice_count: number }[] = [];
	private destroy$ = new Subject<void>();

	readonly DOW    = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	readonly MONTHS = [
		'January','February','March','April','May','June',
		'July','August','September','October','November','December',
	];

	// ── Drill-down state ────────────────────────────────────────────────
	drillOpen      = false;
	drillLevel: DrillLevel = 'brands';
	drillLoading   = false;

	drillDate      = '';    // YYYY-MM-DD
	drillDateLabel = '';    // "Monday, Jan 15"

	drillBrandId:   number | null = null;
	drillBrandName  = '';
	drillDebtor     = '';
	drillDebtorName = '';
	drillTransno    = '';

	drillBrands:    any[] = [];
	drillCustomers: any[] = [];
	drillInvoices:  any[] = [];
	drillLines:     any[] = [];

	drillBrandTotal    = 0;
	drillCustomerTotal = 0;
	drillInvoiceTotal  = 0;

	// Salesman branch state
	drillMode: DrillMode = 'brand';
	drillSalesmanCode = '';
	drillSalesmanName = '';
	drillSalesmen:    any[] = [];
	drillSalesmenTotal = 0;
	drillSalesmenMtdTotal = 0;

	/** The DayCell that was clicked to open the modal — drives KPI ribbon. */
	drillDayCell: DayCell | null = null;
	/** 1-based rank of this day within the current month's sales days (1 = best). */
	drillDayRank   = 0;
	drillDayRankOf = 0;

	// Theme palette — keyed by the `color` value from globalSearchService.
	private static readonly THEME_PALETTE: Record<string, { light: [number, number, number]; base: [number, number, number]; dark: [number, number, number] }> = {
		purple:  { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
		primary: { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
		blue:    { light: [144, 202, 249], base: [ 33, 150, 243], dark: [ 25, 118, 210] },
		info:    { light: [128, 222, 234], base: [  0, 188, 212], dark: [  0,  96, 100] },
		green:   { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
		success: { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
		orange:  { light: [255, 204, 128], base: [255, 152,   0], dark: [230,  81,   0] },
		warning: { light: [255, 224, 130], base: [255, 193,   7], dark: [255, 143,   0] },
		red:     { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
		danger:  { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
		rose:    { light: [244, 143, 177], base: [233,  30,  99], dark: [136,  14,  79] },
	};

	private _themeVarsCache: Record<string, string> | null = null;
	get themeVars(): Record<string, string> {
		if (!this._themeVarsCache) {
			const base = buildDashboardThemeVars('sc', 'primary');
			const p = SalesCalendarComponent.THEME_PALETTE['primary'];
			const rgba = (c: number[], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
			this._themeVarsCache = {
				...base,
				'--sc-backdrop':     `radial-gradient(ellipse at center, ${rgba(p.dark, 0.55)}, rgba(15,5,25,.75))`,
				'--sc-modal-shadow': `0 24px 60px -12px ${rgba(p.dark, 0.5)}`,
			};
		}
		return this._themeVarsCache;
	}

	constructor(
		private reportsService: ReportsService,
		private renderer: Renderer2,
		private globalSearchService: GlobalSearchService,
		private dashboardService: DashboardService,
		private modalService: NgbModal,
	) {}

	// ─── Sales targets ────────────────────────────────────────────────────

	private loadTargets(): void {
		// Seed from localStorage immediately — targets render even if the
		// mytargets endpoint isn't deployed yet or is slow.
		this.targetsByPeriod = this.dashboardService.mergeLocalTargets({}, this.userData);
		this.computeWeekSummaries();
		this.dashboardService.getMySalesTargets()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (r: any) => {
					this.targetsByPeriod = this.dashboardService.mergeLocalTargets(r, this.userData);
					this.computeWeekSummaries();
				},
				error: () => {
					this.targetsByPeriod = this.dashboardService.mergeLocalTargets({}, this.userData);
					this.computeWeekSummaries();
				}
			});
	}

	get canEditTargets(): boolean {
		return !this.isSalesmanUser;
	}

	get monthTarget(): number {
		const period = `${this.viewDate.getFullYear()}-${String(this.viewDate.getMonth() + 1).padStart(2, '0')}`;
		return this.targetsByPeriod[period] || 0;
	}

	get targetAttainment(): number {
		const target = this.monthTarget;
		return target > 0 ? this.totalMonth / target : 0;
	}

	/** Fraction of the viewed month elapsed — the expected pace. Past months 1, future 0. */
	get targetPace(): number {
		const now = new Date();
		const y = this.viewDate.getFullYear();
		const m = this.viewDate.getMonth();
		if (y < now.getFullYear() || (y === now.getFullYear() && m < now.getMonth())) { return 1; }
		if (y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth())) { return 0; }
		const daysInMonth = new Date(y, m + 1, 0).getDate();
		return now.getDate() / daysInMonth;
	}

	get targetFillPct(): number {
		return Math.min(this.targetAttainment, 1) * 100;
	}

	get targetPacePct(): number {
		return Math.min(this.targetPace, 1) * 100;
	}

	get onPace(): boolean {
		return this.targetAttainment >= this.targetPace;
	}

	openTargetEditor(): void {
		this.modalService.open(SalesTargetEditorComponent, { size: 'lg', animation: false })
			.result.then((result) => {
				if (result === 'saved') {
					this.loadTargets();
				}
			}, () => {});
	}

	// Teleport the drill modal to <body> so position:fixed isn't trapped by
	// the dashboard's transformed ancestor (flipState animation), which would
	// constrain the backdrop's containing block and hide the centered panel.
	ngAfterViewChecked(): void {
		const el = this.drillBackdropEl?.nativeElement;
		if (el && el.parentNode !== document.body) {
			this.renderer.appendChild(document.body, el);
		}
	}

	ngOnInit(): void {
		if (this.initialDate) {
			const d = new Date(this.initialDate);
			if (!isNaN(d.getTime())) { this.viewDate = d; }
		}
		this.globalSearchService.user.pipe(takeUntil(this.destroy$)).subscribe((u: any) => {
			this.userData = u;
			this.isSalesmanUser = !!u?.user?.issalesman;
		});
		this.globalSearchService.configsubscription.pipe(takeUntil(this.destroy$)).subscribe((c: any) => {
			const wasUnits = this.unitsPrimary;
			this.unitsPrimary = c?.env?.package === 'tires';
			// Heat-map + ranking key changes when the lead metric flips — rebuild.
			if (wasUnits !== this.unitsPrimary && !this.loading) { this.buildCalendar(); }
		});
		this.loadTargets();
		this.reportsService.getDaySales()
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.rawData = (r || []).map((row: any) => ({
					trandate:      row.trandate,
					invoiced:      parseFloat(row.invoiced)    || 0,
					dollars:       parseFloat(row.dollars)     || 0,
					invoice_count: parseInt(row.invoice_count) || 0,
				}));
				this.loading = false;
				this.buildCalendar();
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ── Navigation ──────────────────────────────────────────────────────

	get monthLabel(): string {
		return `${this.MONTHS[this.viewDate.getMonth()]} ${this.viewDate.getFullYear()}`;
	}

	get isViewingCurrentMonth(): boolean {
		const now = new Date();
		return this.viewDate.getFullYear() === now.getFullYear()
			&& this.viewDate.getMonth() === now.getMonth();
	}

	prevMonth(): void {
		const d = new Date(this.viewDate);
		d.setMonth(d.getMonth() - 1);
		this.viewDate = d;
		this.closeDrill();
		this.buildCalendar();
	}

	nextMonth(): void {
		const d = new Date(this.viewDate);
		d.setMonth(d.getMonth() + 1);
		this.viewDate = d;
		this.closeDrill();
		this.buildCalendar();
	}

	goToday(): void {
		this.viewDate = new Date();
		this.closeDrill();
		this.buildCalendar();
	}

	// ── Calendar build ──────────────────────────────────────────────────

	private rowForDate(d: Date): { dollars: number; units: number; invoices: number } {
		const key = d.toLocaleDateString('en-CA');
		const hit = this.rawData.find(r => r.trandate === key);
		return hit
			? { dollars: hit.dollars, units: hit.invoiced, invoices: hit.invoice_count }
			: { dollars: 0, units: 0, invoices: 0 };
	}

	private priorYearRowForDate(d: Date): { dollars: number; units: number; invoices: number } {
		const prior = new Date(d);
		prior.setFullYear(prior.getFullYear() - 1);
		return this.rowForDate(prior);
	}

	/** Returns the best non-zero value for heatmap/scaling — dollars if available, invoice count otherwise */
	private effectiveValue(row: { dollars: number; units: number; invoices: number }): number {
		return row.dollars > 0 ? row.dollars : row.invoices;
	}

	private buildCalendar(): void {
		const year  = this.viewDate.getFullYear();
		const month = this.viewDate.getMonth();
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		// Collect current-month sales for stats + scaling
		const monthlyValues: number[] = [];
		let   priorMonthTotal = 0;
		let   priorMonthUnits = 0;
		let   monthUnits = 0;
		let   bestVal = 0;
		let   bestDate: Date | null = null;
		let   bestUnitsVal = 0;
		let   bestUnitsDate: Date | null = null;

		const cursor = new Date(year, month, 1);
		while (cursor.getMonth() === month) {
			const cur  = this.rowForDate(cursor);
			const prev = this.priorYearRowForDate(cursor);
			const val  = this.effectiveValue(cur);
			if (val > 0) {
				monthlyValues.push(val);
				if (val > bestVal) { bestVal = val; bestDate = new Date(cursor); }
			}
			if (cur.units > bestUnitsVal) { bestUnitsVal = cur.units; bestUnitsDate = new Date(cursor); }
			monthUnits      += cur.units;
			priorMonthTotal += prev.dollars > 0 ? prev.dollars : prev.units;
			priorMonthUnits += prev.units;
			cursor.setDate(cursor.getDate() + 1);
		}

		this.totalMonth   = monthlyValues.reduce((a, b) => a + b, 0);
		this.totalUnits   = 0;  // filled during cell build below
		this.bestDay      = bestVal;
		this.bestDayDate  = bestDate;
		this.bestUnits    = bestUnitsVal;
		this.bestUnitsDate = bestUnitsDate;
		this.salesDays   = monthlyValues.length;
		this.avgDay      = this.salesDays ? this.totalMonth / this.salesDays : 0;
		// YoY keys off units when units lead (tires), else dollars.
		this.monthPct    = this.unitsPrimary
			? (priorMonthUnits > 0 ? (monthUnits - priorMonthUnits) / priorMonthUnits : null)
			: (priorMonthTotal > 0 ? (this.totalMonth - priorMonthTotal) / priorMonthTotal : null);

		// Build 6-week grid
		const firstOfMonth = new Date(year, month, 1);
		const startCell    = new Date(firstOfMonth);
		startCell.setDate(1 - firstOfMonth.getDay());

		this.weeks = [];
		let unitSum = 0;
		const cell = new Date(startCell);

		for (let w = 0; w < 6; w++) {
			const week: DayCell[] = [];
			for (let d = 0; d < 7; d++) {
				const cur     = this.rowForDate(cell);
				const prev    = this.priorYearRowForDate(cell);
				const curVal  = this.effectiveValue(cur);
				const prevVal = this.effectiveValue(prev);
				const pct     = prevVal > 0 ? (curVal - prevVal) / prevVal : null;

				if (cell.getMonth() === month && cur.units > 0) { unitSum += cur.units; }

				week.push({
					date:           new Date(cell),
					dayNum:         cell.getDate(),
					isCurrentMonth: cell.getMonth() === month,
					isToday:        cell.getTime() === today.getTime(),
					dollars:        cur.dollars,
					units:          cur.units,
					invoices:       cur.invoices,
					priorDollars:   prev.dollars,
					priorUnits:     prev.units,
					dollarPct:      pct,
					intensity:      this.unitsPrimary
						? (this.bestUnits > 0 ? cur.units / this.bestUnits : 0)
						: (this.bestDay > 0 ? curVal / this.bestDay : 0),
				});
				cell.setDate(cell.getDate() + 1);
			}
			this.weeks.push(week);
		}

		this.totalUnits = unitSum;
		this.computeWeekSummaries();
	}

	/** Where the month stands against its target, week by week. */
	private computeWeekSummaries(): void {
		const target = this.monthTarget;
		if (!target || !this.weeks.length) {
			this.weekSummaries = [];
			return;
		}

		const month = this.viewDate.getMonth();
		const year = this.viewDate.getFullYear();
		const daysInMonth = new Date(year, month + 1, 0).getDate();
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const fmt = (d: Date) => `${this.MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}`;

		let cumTotal = 0;
		const summaries: Array<{ label: string; total: number; cumTotal: number; cumPct: number; expectedPct: number; started: boolean; isCurrent: boolean }> = [];
		for (const week of this.weeks) {
			const inMonth = week.filter(d => d.isCurrentMonth);
			if (!inMonth.length) { continue; }

			const total = inMonth.reduce((sum, d) => sum + (d.dollars || 0), 0);
			cumTotal += total;

			const weekStart = inMonth[0].date;
			const weekEnd = inMonth[inMonth.length - 1].date;
			// Expected attainment by end of this week (capped at today for the current month).
			const paceThrough = weekEnd > today ? (weekStart <= today ? today : weekStart) : weekEnd;
			const expectedDays = paceThrough >= weekStart ? Math.min(paceThrough.getDate(), daysInMonth) : 0;

			summaries.push({
				label: `${fmt(weekStart)}–${fmt(weekEnd)}`,
				total,
				cumTotal,
				cumPct: cumTotal / target,
				expectedPct: expectedDays / daysInMonth,
				started: weekStart <= today,
				isCurrent: weekStart <= today && today <= weekEnd,
			});
		}
		this.weekSummaries = summaries;
	}

	// ── Formatting helpers ───────────────────────────────────────────────

	/** Format dollar value; if dollars not available show invoice count as fallback */
	formatDollars(dollars: number, invoices: number): string {
		if (dollars > 0) {
			if (dollars >= 1_000_000) { return '$' + (dollars / 1_000_000).toFixed(1) + 'M'; }
			if (dollars >= 1000)      { return '$' + (dollars / 1000).toFixed(1) + 'k'; }
			return '$' + dollars.toFixed(0);
		}
		// Fallback: show invoice count so the cell isn't meaningless
		return invoices > 0 ? invoices + ' inv' : '';
	}

	formatUnits(n: number): string {
		return n > 0 ? n.toFixed(0) + ' u' : '';
	}

	formatPct(pct: number | null): string {
		if (pct === null) { return ''; }
		const sign = pct >= 0 ? '+' : '';
		return sign + (pct * 100).toFixed(0) + '%';
	}

	pctClass(pct: number | null): string {
		if (pct === null) { return ''; }
		return pct >= 0 ? 'sc-pct--up' : 'sc-pct--down';
	}

	cellBg(intensity: number): string {
		if (intensity <= 0) { return ''; }
		const t = Math.min(intensity, 1);
		// Interpolate white → accent.dark using the user's chosen theme color.
		const [dr, dg, db] = SalesCalendarComponent.THEME_PALETTE['primary'].dark;
		const r = Math.round(255 - t * (255 - dr));
		const g = Math.round(255 - t * (255 - dg));
		const b = Math.round(255 - t * (255 - db));
		return `rgb(${r},${g},${b})`;
	}

	cellTextColor(intensity: number): string {
		return intensity > 0.32 ? '#fff' : '#212529';
	}

	// ── Day click → drill-down ───────────────────────────────────────────

	onDayClick(day: DayCell): void {
		if (!day.isCurrentMonth || day.invoices === 0) { return; }
		this.drillDate      = day.date.toLocaleDateString('en-CA');
		this.drillDateLabel = day.date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
		this.drillBrandId   = null;
		this.drillBrandName = '';
		this.drillDebtor    = '';
		this.drillDebtorName = '';
		this.drillTransno   = '';
		this.drillSalesmanCode = '';
		this.drillSalesmanName = '';
		this.drillMode      = 'brand';
		this.drillLevel     = 'brands';
		this.drillOpen      = true;
		this.drillDayCell   = day;
		this.computeDayRank(day);
		this.loadBrands();
	}

	/** Compute "best day of month" rank — 1 = highest among sales days. Ranks by
	 *  units when units lead (tires), else by dollars. */
	private computeDayRank(day: DayCell): void {
		const metric = (c: DayCell) => this.unitsPrimary ? c.units : c.dollars;
		const ranked = this.weeks.flat()
			.filter(c => c.isCurrentMonth && metric(c) > 0)
			.map(metric)
			.sort((a, b) => b - a);
		this.drillDayRankOf = ranked.length;
		this.drillDayRank   = Math.max(1, ranked.indexOf(metric(day)) + 1);
	}

	/** Average order value for the day. */
	get drillAov(): number {
		if (!this.drillDayCell || this.drillDayCell.invoices === 0) { return 0; }
		return this.drillDayCell.dollars / this.drillDayCell.invoices;
	}

	/** % delta vs the month's average sales day (current month). */
	get drillVsMonthAvg(): number | null {
		if (!this.drillDayCell || this.avgDay === 0) { return null; }
		return (this.drillDayCell.dollars - this.avgDay) / this.avgDay;
	}

	/** "Top" entry of the current visible list (highest sales row). */
	get drillTopRow(): { name: string; sales: number } | null {
		let list: any[] = [];
		let nameKey = '';
		switch (this.drillLevel) {
			case 'brands':       list = this.drillBrands;    nameKey = 'brand';        break;
			case 'salesmen':     list = this.drillSalesmen;  nameKey = 'salesmanname'; break;
			case 'customers':
			case 'sm-customers': list = this.drillCustomers; nameKey = 'brname';       break;
			case 'invoices':
			case 'sm-invoices':  list = this.drillInvoices;  nameKey = 'transno';      break;
			default: return null;
		}
		if (!list.length) { return null; }
		const top = list.reduce((a, b) => (parseFloat(b.sales) > parseFloat(a.sales) ? b : a));
		return { name: this.drillLevel === 'invoices' || this.drillLevel === 'sm-invoices' ? `#${top[nameKey]}` : top[nameKey], sales: parseFloat(top.sales) };
	}

	/** Visible list's count + total — used in the "Showing X • Total $Y" strip. */
	get drillListMeta(): { count: number; total: number; label: string } {
		switch (this.drillLevel) {
			case 'brands':       return { count: this.drillBrands.length,    total: this.drillBrandTotal,     label: this.drillBrands.length === 1 ? 'brand' : 'brands' };
			case 'salesmen':     return { count: this.drillSalesmen.length,  total: this.drillSalesmenTotal,  label: this.drillSalesmen.length === 1 ? 'salesman' : 'salesmen' };
			case 'customers':
			case 'sm-customers': return { count: this.drillCustomers.length, total: this.drillCustomerTotal,  label: this.drillCustomers.length === 1 ? 'customer' : 'customers' };
			case 'invoices':
			case 'sm-invoices':  return { count: this.drillInvoices.length,  total: this.drillInvoiceTotal,   label: this.drillInvoices.length === 1 ? 'invoice' : 'invoices' };
			default:             return { count: 0, total: 0, label: '' };
		}
	}

	switchMode(mode: DrillMode): void {
		if (this.drillMode === mode) { return; }
		this.drillMode         = mode;
		this.drillBrandId      = null;
		this.drillBrandName    = '';
		this.drillDebtor       = '';
		this.drillDebtorName   = '';
		this.drillTransno      = '';
		this.drillSalesmanCode = '';
		this.drillSalesmanName = '';
		if (mode === 'brand') {
			this.drillLevel = 'brands';
			this.loadBrands();
		} else {
			this.drillLevel = 'salesmen';
			this.loadSalesmen();
		}
	}

	private loadSalesmen(): void {
		this.drillLoading = true;
		this.drillSalesmen = [];
		this.reportsService.getDaySalesmen(this.drillDate)
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.drillSalesmen          = r || [];
				this.drillSalesmenTotal     = this.drillSalesmen.reduce((s: number, x: any) => s + parseFloat(x.sales), 0);
				this.drillSalesmenMtdTotal  = this.drillSalesmen.reduce((s: number, x: any) => s + parseFloat(x.mtd_sales), 0);
				this.drillLoading           = false;
			});
	}

	onSalesmanClick(sm: any): void {
		this.drillSalesmanCode = sm.salesmancode;
		this.drillSalesmanName = sm.salesmanname;
		this.drillLevel        = 'sm-customers';
		this.drillLoading      = true;
		this.drillCustomers    = [];
		this.reportsService.getDaySalesmanCustomers(this.drillDate, this.drillSalesmanCode)
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.drillCustomers     = r || [];
				this.drillCustomerTotal = this.drillCustomers.reduce((s: number, c: any) => s + parseFloat(c.sales), 0);
				this.drillLoading       = false;
			});
	}

	onSalesmanCustomerClick(cust: any): void {
		this.drillDebtor     = cust.debtorno;
		this.drillDebtorName = cust.brname;
		this.drillLevel      = 'sm-invoices';
		this.drillLoading    = true;
		this.drillInvoices   = [];
		this.reportsService.getDaySalesmanInvoices(this.drillDate, this.drillSalesmanCode, this.drillDebtor)
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.drillInvoices     = r || [];
				this.drillInvoiceTotal = this.drillInvoices.reduce((s: number, i: any) => s + parseFloat(i.sales), 0);
				this.drillLoading      = false;
			});
	}

	onSalesmanInvoiceClick(inv: any): void {
		this.drillTransno = inv.transno;
		this.drillLevel   = 'sm-lines';
		this.drillLoading = true;
		this.drillLines   = [];
		this.reportsService.getInvoiceLineItems(inv.transno)
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.drillLines   = r || [];
				this.drillLoading = false;
			});
	}

	salesmanYoy(sm: any): number | null {
		const cur   = parseFloat(sm.sales) || 0;
		const prior = parseFloat(sm.prior_sales) || 0;
		if (prior <= 0) { return null; }
		return (cur - prior) / prior;
	}

	private loadBrands(): void {
		this.drillLoading = true;
		this.drillBrands  = [];
		this.reportsService.getDayBrands(this.drillDate)
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.drillBrands       = r || [];
				this.drillBrandTotal   = this.drillBrands.reduce((s: number, b: any) => s + parseFloat(b.sales), 0);
				this.drillLoading      = false;
			});
	}

	onBrandClick(brand: any): void {
		this.drillBrandId    = brand.manufacturer_id;
		this.drillBrandName  = brand.brand;
		this.drillLevel      = 'customers';
		this.drillLoading    = true;
		this.drillCustomers  = [];
		this.reportsService.getDayBrandCustomers(this.drillDate, this.drillBrandId)
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.drillCustomers     = r || [];
				this.drillCustomerTotal = this.drillCustomers.reduce((s: number, c: any) => s + parseFloat(c.sales), 0);
				this.drillLoading       = false;
			});
	}

	onCustomerClick(cust: any): void {
		this.drillDebtor     = cust.debtorno;
		this.drillDebtorName = cust.brname;
		this.drillLevel      = 'invoices';
		this.drillLoading    = true;
		this.drillInvoices   = [];
		this.reportsService.getDayCustomerInvoices(this.drillDate, this.drillDebtor, this.drillBrandId)
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.drillInvoices     = r || [];
				this.drillInvoiceTotal = this.drillInvoices.reduce((s: number, i: any) => s + parseFloat(i.sales), 0);
				this.drillLoading      = false;
			});
	}

	onInvoiceClick(inv: any): void {
		this.drillTransno = inv.transno;
		this.drillLevel   = 'lines';
		this.drillLoading = true;
		this.drillLines   = [];
		this.reportsService.getInvoiceLineItems(inv.transno)
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.drillLines   = r || [];
				this.drillLoading = false;
			});
	}

	breadcrumbBack(target: DrillLevel): void {
		this.drillLevel = target;
	}

	/** True if there's a level above the current one (i.e. drill-up is meaningful). */
	get canDrillBack(): boolean {
		return this.drillLevel !== 'brands' && this.drillLevel !== 'salesmen';
	}

	/** One step up the drill hierarchy. Closes the modal if already at root. */
	drillBack(): void {
		switch (this.drillLevel) {
			case 'customers':    this.drillLevel = 'brands';        break;
			case 'invoices':     this.drillLevel = 'customers';     break;
			case 'lines':        this.drillLevel = 'invoices';      break;
			case 'sm-customers': this.drillLevel = 'salesmen';      break;
			case 'sm-invoices':  this.drillLevel = 'sm-customers';  break;
			case 'sm-lines':     this.drillLevel = 'sm-invoices';   break;
			default:             this.closeDrill();
		}
	}

	closeDrill(): void {
		this.drillOpen = false;
	}

	/** Width bar for brand/customer rows — % of day's total */
	salesBar(sales: number, total: number): number {
		return total > 0 ? Math.min((sales / total) * 100, 100) : 0;
	}

	lineTotal(lines: any[]): number {
		return lines.reduce((s, l) => s + (parseFloat(l.linetotal) || 0), 0);
	}
}
